import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { configurationProfileApi } from '../../../api/production/configuration-profiles';
import { technologyApi } from '../../../api/production/technologies';
import { ConfigurationProfileUpsertForm } from '../../../components/forms/production/config-profile/upsert-form';
import { Skeleton } from '../../../components/ui/skeleton';
import { getConfigurationProfileStatus } from '../../../types/production/configuration-profile-list.type';

export function ConfigurationProfileEditPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const {
    data: configurationProfile,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useQuery({
    queryKey: ['configuration-profile', id],
    queryFn: () => configurationProfileApi.findOne(id!),
    enabled: !!id,
  });

  const { data: technologiesData, isLoading: isLoadingTechnologies } = useQuery({
    queryKey: ['technologies'],
    queryFn: () => technologyApi.findMany({}),
  });

  const { data: technology, isLoading: isLoadingTechnology } = useQuery({
    queryKey: ['technology', configurationProfile?.technology_id],
    queryFn: () => technologyApi.findOne(configurationProfile?.technology_id!),
    enabled: !!configurationProfile?.technology_id,
  });

  const isLoading = isLoadingProfile || isLoadingTechnologies || isLoadingTechnology;

  const technologies = technologiesData?.docs || [];
  const status = configurationProfile
    ? getConfigurationProfileStatus(configurationProfile)
    : undefined;
  const pageTitle =
    status === 'AGUARDANDO_VALIDACAO'
      ? 'Validação de Perfil de Configuração'
      : 'Detalhes do Perfil de Configuração';
  const breadcrumb =
    status === 'AGUARDANDO_VALIDACAO'
      ? 'Produção > Perfis de Configuração > Validação de Perfil de Configuração'
      : 'Produção > Perfis de Configuração > Detalhes do Perfil de Configuração';

  if (!id) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
          <div>
            <h1 className="text-base font-semibold leading-7 text-gray-900">
              ID do perfil não fornecido
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-4xl space-y-8">
          <div className="space-y-2">
            <Skeleton className="h-9 w-96" />
            <Skeleton className="h-5 w-64" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!configurationProfile || profileError) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
          <div>
            <h1 className="text-base font-semibold leading-7 text-gray-900">
              Nenhum perfil de configuração encontrado
            </h1>
            {profileError && (
              <p className="mt-2 text-sm text-red-600">
                Erro: {profileError instanceof Error ? profileError.message : 'Erro desconhecido'}
                {id && <span className="block mt-1">ID: {id}</span>}
              </p>
            )}
            {!id && <p className="mt-2 text-sm text-yellow-600">Nenhum ID foi fornecido na URL</p>}
          </div>
        </div>
      </div>
    );
  }

  if (!technology) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
          <div>
            <h1 className="text-base font-semibold leading-7 text-gray-900">
              Algumas informações do perfil não foram encontradas
            </h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{breadcrumb}</p>
          <h1 className="text-3xl font-bold tracking-tight">{pageTitle}</h1>
          <p className="text-muted-foreground">
            Atualizar o perfil para configuração de equipamentos IoT
          </p>
        </div>
        <ConfigurationProfileUpsertForm
          technologies={technologies}
          defaultValues={{ configurationProfile, technology }}
          isEdit
        />
      </div>
    </div>
  );
}

export default ConfigurationProfileEditPage;
